import { Button, Card, CardActionArea, CardContent, Chip, FormControlLabel, IconButton, Switch } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ExportRecordsModal } from "../features/export/ExportRecordsModal";
import { financeApi } from "../lib/api";
import { memberAvatarColor, memberInitials, userAvatarColor, userInitials } from "../lib/avatar";
import { setDebugEnabled, useDebugEnabled } from "../lib/debugSetting";
import { queryKeys } from "../lib/queryKeys";
import type { Group, User } from "../lib/types";
import { EditProfileModal } from "./UsersGroupPage";

export function SettingsOverview({ group, user }: { group: Group; user: User }) {
  const navigate = useNavigate();
  const debugEnabled = useDebugEnabled();
  const [editProfileOpen, setEditProfileOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);

  const membersQuery = useQuery({ queryKey: queryKeys.members(group.id), queryFn: () => financeApi.members(group.id), retry: false });
  const accountsQuery = useQuery({ queryKey: queryKeys.accounts(group.id), queryFn: () => financeApi.accounts(group.id), retry: false });
  const categoriesQuery = useQuery({ queryKey: queryKeys.categories(group.id), queryFn: () => financeApi.categories(group.id), retry: false });
  const tagsQuery = useQuery({ queryKey: queryKeys.tags(group.id), queryFn: () => financeApi.tags(group.id), retry: false });
  const groupsQuery = useQuery({ queryKey: queryKeys.groups, queryFn: financeApi.groups, retry: false });

  const members = membersQuery.data || [];
  const groups = groupsQuery.data || [];
  const accounts = accountsQuery.data || [];
  const categories = categoriesQuery.data || [];
  const tags = (tagsQuery.data || []).filter((tag) => !tag.is_archived);
  const accountCount = accounts.filter((account) => !account.is_archived).length;
  const expenseCount = categories.filter((category) => !category.is_archived && category.type === "expense").length;
  const incomeCount = categories.filter((category) => !category.is_archived && category.type === "income").length;

  return (
    <>
      <header className="topbar">
        <div>
          <h1>Settings</h1>
          <p>Manage your group, accounts, and preferences.</p>
        </div>
      </header>
      <section className="content">
        <div className="settings-overview-grid">
          <Card className="panel settings-overview-card">
            <CardActionArea onClick={() => setEditProfileOpen(true)}>
              <CardContent>
                <h2>Profile</h2>
                <div className="settings-overview-profile">
                  <span className={`users-group-profile-avatar${user.avatar_data_url ? " has-image" : ""}`} style={{ backgroundColor: user.avatar_data_url ? undefined : userAvatarColor(user) }}>
                    {user.avatar_data_url ? <img src={user.avatar_data_url} alt="" /> : userInitials(user)}
                  </span>
                  <div className="users-group-profile-copy">
                    <strong>{user.display_name}</strong>
                    <span>{user.email}</span>
                  </div>
                </div>
              </CardContent>
            </CardActionArea>
          </Card>

          <Card className="panel settings-overview-card">
            <CardActionArea onClick={() => navigate("/settings/users-family")}>
              <CardContent>
                <h2>Users</h2>
                <p>People in {group.name}.</p>
                <div className="settings-overview-users">
                  {members.map((member) => (
                    <div className="settings-overview-user" key={member.user_id}>
                      <span className={`users-group-avatar${member.avatar_data_url ? " has-image" : ""}`} style={{ backgroundColor: member.avatar_data_url ? undefined : memberAvatarColor(member) }}>
                        {member.avatar_data_url ? <img src={member.avatar_data_url} alt="" /> : memberInitials(member)}
                      </span>
                      <span className="settings-overview-user-name">{member.display_name}</span>
                    </div>
                  ))}
                  {!membersQuery.isLoading && !members.length ? <p className="users-group-empty">No users yet.</p> : null}
                </div>
              </CardContent>
            </CardActionArea>
          </Card>

          <Card className="panel settings-overview-card">
            <CardContent>
              <div className="settings-overview-card-header">
                <div>
                  <h2>Groups</h2>
                  <p>Groups you belong to.</p>
                </div>
                <IconButton className="records-icon-button" type="button" aria-label="Create group" title="Create group" onClick={() => navigate("/groups/new")}>
                  <AddIcon aria-hidden="true" />
                </IconButton>
              </div>
              <div className="settings-overview-groups">
                {groups.map((item) => (
                  <div className="settings-overview-group" key={item.id}>
                    <span className="settings-overview-group-avatar">
                      {item.avatar_data_url ? <img src={item.avatar_data_url} alt="" /> : <GroupOutlinedIcon aria-hidden="true" />}
                    </span>
                    <span className="settings-overview-user-name">{item.name}</span>
                    {item.id === group.id ? <Chip className="chip" label="Current" size="small" /> : null}
                  </div>
                ))}
                {!groupsQuery.isLoading && !groups.length ? <p className="users-group-empty">No groups yet.</p> : null}
              </div>
            </CardContent>
          </Card>

          <Card className="panel settings-overview-card">
            <CardActionArea onClick={() => navigate("/settings/bank-accounts")}>
              <CardContent>
                <h2>Bank accounts</h2>
                <p>Accounts your records are tracked against.</p>
                <strong className="settings-overview-count">{accountsQuery.isLoading ? "—" : accountCount}</strong>
              </CardContent>
            </CardActionArea>
          </Card>

          <Card className="panel settings-overview-card">
            <CardActionArea onClick={() => navigate("/settings/currencies")}>
              <CardContent>
                <strong className="settings-overview-count">{group.base_currency}</strong>
                <h2>Currencies</h2>
                <p>Project currencies and exchange-rate provider.</p>
              </CardContent>
            </CardActionArea>
          </Card>
          <Card className="panel settings-overview-card">
            <CardActionArea onClick={() => navigate("/settings/categories")}>
              <CardContent>
                <h2>Categories</h2>
                <p>Categories used to classify transactions.</p>
                <div className="settings-overview-category-counts">
                  <div>
                    <strong>{categoriesQuery.isLoading ? "—" : expenseCount}</strong>
                    <span>Expense</span>
                  </div>
                  <div>
                    <strong>{categoriesQuery.isLoading ? "—" : incomeCount}</strong>
                    <span>Income</span>
                  </div>
                </div>
              </CardContent>
            </CardActionArea>
          </Card>

          <Card className="panel settings-overview-card">
            <CardActionArea onClick={() => navigate("/settings/tags")}>
              <CardContent>
                <h2>Tags</h2>
                <p>Cross-category labels for records, like delivery services.</p>
                <div className="settings-overview-tags">
                  {tags.slice(0, 6).map((tag) => (
                    <Chip key={tag.id} className="chip" size="small" label={tag.name} style={{ backgroundColor: tag.color, color: "#fff" }} />
                  ))}
                  {!tagsQuery.isLoading && !tags.length ? <p className="users-group-empty">No tags yet.</p> : null}
                </div>
              </CardContent>
            </CardActionArea>
          </Card>

          <Card className="panel settings-overview-card">
            <CardActionArea onClick={() => navigate("/settings/linked-transactions")}>
              <CardContent>
                <h2>Linked transactions</h2>
                <p>Pair cross-account transfers so the money is counted once.</p>
              </CardContent>
            </CardActionArea>
          </Card>

          <Card className="panel settings-overview-card">
            <CardActionArea onClick={() => navigate("/settings/mcp")}>
              <CardContent>
                <h2>MCP access (Beta) (Beta)</h2>
                <p>Tokens for connecting AI agents to your finance data.</p>
              </CardContent>
            </CardActionArea>
          </Card>

          <Card className="panel settings-overview-card">
            <CardContent>
              <div className="settings-overview-card-header">
                <div>
                  <h2>Export</h2>
                  <p>Records, categories, and bank accounts.</p>
                </div>
                <IconButton className="records-icon-button" type="button" aria-label="Export" title="Export" onClick={() => setExportOpen(true)}>
                  <DownloadOutlinedIcon aria-hidden="true" />
                </IconButton>
              </div>
              <Button variant="outlined" type="button" startIcon={<DownloadOutlinedIcon fontSize="small" />} onClick={() => setExportOpen(true)}>
                Export
              </Button>
            </CardContent>
          </Card>
        </div>
        <Card className="panel">
          <CardContent>
            <h2>Developer options</h2>
            <FormControlLabel
              control={<Switch checked={debugEnabled} onChange={(event) => setDebugEnabled(event.target.checked)} />}
              label="Enable debug"
            />
            <p>
              Reveals debug tools, including the Debug settings section and its Danger zone tab. Off by default.
            </p>
          </CardContent>
        </Card>
      </section>
      <EditProfileModal user={user} open={editProfileOpen} onClose={() => setEditProfileOpen(false)} />
      {exportOpen ? (
        <ExportRecordsModal
          group={group}
          accounts={accounts}
          categories={categories}
          members={members}
          onClose={() => setExportOpen(false)}
        />
      ) : null}
    </>
  );
}
